import { useTranslation } from "react-i18next";
import { XMarkIcon, LinkIcon } from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badge";
import type { View } from "./types";
import { VIEW_LABELS, VIEW_COLORS } from "./types";
import type { UnifiedEvent } from "./data/events";
import { UNIFIED_EVENTS } from "./data/events";

export function InvestigatePanel({
    event,
    onClose,
    onNavigate,
}: {
    event: UnifiedEvent;
    onClose: () => void;
    onNavigate: (v: View) => void;
}) {
    const { t } = useTranslation();
    const relatedEvents = UNIFIED_EVENTS.filter(
        (e) =>
            e.id !== event.id &&
            (event.correlated.includes(e.system) ||
                e.correlated.includes(event.system) ||
                (event.actor && e.actor === event.actor)),
    );

    return (
        <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-zinc-200/30 bg-white/80 shadow-2xl shadow-blue-500/10 backdrop-blur-2xl dark:border-zinc-700/30 dark:bg-zinc-900/80">
            <div className="flex items-center justify-between border-b border-zinc-200/30 bg-gradient-to-r from-blue-600/5 via-purple-600/5 to-transparent px-6 py-5 dark:border-zinc-700/30 dark:from-blue-500/10 dark:via-purple-500/5 dark:to-transparent">
                <div>
                    <h3 className="bg-gradient-to-r from-zinc-900 to-zinc-600 bg-clip-text text-base font-bold text-transparent dark:from-white dark:to-zinc-400">
                        {t("investigate.title")}
                    </h3>
                    <p className="mt-0.5 font-mono text-xs text-zinc-400">{event.id}</p>
                </div>
                <button
                    onClick={onClose}
                    className="rounded-xl p-1.5 transition-all duration-200 hover:bg-zinc-100/80 hover:shadow-sm dark:hover:bg-zinc-800/80"
                >
                    <XMarkIcon className="h-5 w-5 text-zinc-400" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
                <div className="rounded-2xl border border-zinc-200/40 bg-zinc-50/70 p-5 backdrop-blur-sm dark:border-zinc-700/40 dark:bg-zinc-800/50">
                    <div className="flex items-center gap-2">
                        <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                        <Badge
                            color={
                                event.severity === "critical"
                                    ? "red"
                                    : event.severity === "high"
                                      ? "orange"
                                      : event.severity === "medium"
                                        ? "yellow"
                                        : "zinc"
                            }
                        >
                            {event.severity.toUpperCase()}
                        </Badge>
                    </div>
                    <p className="mt-3 text-sm font-semibold text-zinc-900 dark:text-white">{event.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{event.details}</p>
                    {event.actor && (
                        <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200/40 bg-gradient-to-r from-red-50/80 to-red-50/30 px-3.5 py-2.5 shadow-sm shadow-red-500/5 backdrop-blur-sm dark:border-red-900/30 dark:from-red-950/40 dark:to-red-950/20">
                            <span className="text-xs font-semibold text-red-600 dark:text-red-400">Threat Actor</span>
                            <Badge color="red">{event.actor}</Badge>
                        </div>
                    )}
                    <div className="mt-4">
                        <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                            Correlated Systems
                        </span>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {event.correlated.map((sys) => (
                                <button key={sys} onClick={() => onNavigate(sys)}>
                                    <Badge color={VIEW_COLORS[sys]}>
                                        <LinkIcon className="mr-0.5 inline h-2.5 w-2.5" />
                                        {VIEW_LABELS[sys]}
                                    </Badge>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-6">
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
                        {t("investigate.correlatedEvents")} ({relatedEvents.length})
                    </span>
                    <div className="mt-3 space-y-0">
                        {relatedEvents.map((re) => (
                            <div
                                key={re.id}
                                className="relative border-l-2 border-blue-200/60 py-3 pl-5 dark:border-blue-800/60"
                            >
                                <div className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full bg-blue-400 shadow-sm shadow-blue-400/50" />
                                <div className="flex items-center gap-2">
                                    <Badge color={VIEW_COLORS[re.system]}>{VIEW_LABELS[re.system]}</Badge>
                                    <span className="font-mono text-xs text-zinc-400">{re.id}</span>
                                    <span className="font-mono text-xs text-zinc-400">{re.time}</span>
                                </div>
                                <p className="mt-1 text-sm font-medium text-zinc-700 dark:text-zinc-300">{re.title}</p>
                                <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">{re.details}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
